import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { to12h } from '../../utils/time';

export default function BarberCalendarPage() {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [dayAppts, setDayAppts] = useState([]);
  const [updating, setUpdating] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startOffset = firstDay === 0 ? 6 : firstDay - 1;
  const monthNames = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

  const loadMonth = useCallback(async () => {
    setLoading(true);
    try {
      const start = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const end = `${year}-${String(month + 1).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
      // /range filtra automáticamente por trabajador_id cuando rol=barbero
      const res = await api.get('/appointments/range', { params: { start, end } });
      setAppointments(res.data);
    } catch { setAppointments([]); }
    finally { setLoading(false); }
  }, [year, month, daysInMonth]);

  useEffect(() => { loadMonth(); }, [loadMonth]);

  const getAppts = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return appointments.filter(a => a.appointment_date?.slice(0, 10) === dateStr);
  };

  const selectDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelected(dateStr);
    setDayAppts(getAppts(day));
  };

  const changeStatus = async (apptId, newStatus) => {
    setUpdating(apptId);
    try {
      await api.patch(`/appointments/${apptId}/status`, { status: newStatus });
      loadMonth();
      if (selected) {
        const day = parseInt(selected.split('-')[2]);
        setDayAppts(getAppts(day));
      }
    } catch {} finally { setUpdating(null); }
  };

  const isToday = (day) => {
    const t = new Date();
    return t.getDate() === day && t.getMonth() === month && t.getFullYear() === year;
  };

  const statusColor = (s) => ({ confirmed:'#2563eb', cancelled:'#dc2626', attended:'#16a34a', not_attended:'#64748b', rescheduled:'#8b5cf6' }[s] || '#888');
  const statusLabel = (s) => ({ confirmed:'Confirmada', cancelled:'Cancelada', attended:'Atendida', not_attended:'No asistió', rescheduled:'Reagendada' }[s] || s);

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const todayAppts = appointments.filter(a => {
    const t = new Date();
    return a.appointment_date?.slice(0, 10) === t.toISOString().slice(0, 10);
  });

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Mi Calendario</h1>
          <p className="page-subtitle">Tus citas asignadas</p>
        </div>
      </div>

      {/* Today summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <div className="stat-card">
          <div className="stat-value">{todayAppts.length}</div>
          <div className="stat-label">Citas hoy</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{todayAppts.filter(a => a.status === 'confirmed').length}</div>
          <div className="stat-label">Confirmadas hoy</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{todayAppts.filter(a => a.status === 'confirmed' || a.status === 'attended').length}</div>
          <div className="stat-label">Confirmadas</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{appointments.length}</div>
          <div className="stat-label">Este mes</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 320px' : '1fr', gap: '1.5rem' }}>
        <div className="card" style={{ padding: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <button className="btn btn-ghost" onClick={() => setCurrentDate(new Date(year, month - 1, 1))}>‹</button>
            <h3 style={{ margin: 0, color: 'var(--color-gold)', fontFamily: 'var(--font-display)' }}>{monthNames[month]} {year}</h3>
            <button className="btn btn-ghost" onClick={() => setCurrentDate(new Date(year, month + 1, 1))}>›</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', marginBottom: '0.5rem' }}>
            {['L','M','X','J','V','S','D'].map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-text-muted)' }}>{d}</div>
            ))}
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>Cargando...</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '4px' }}>
              {cells.map((day, idx) => {
                if (!day) return <div key={idx} />;
                const appts = getAppts(day);
                const isSelected = selected === `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                return (
                  <div
                    key={day}
                    onClick={() => selectDay(day)}
                    style={{
                      minHeight: '60px', padding: '6px', borderRadius: '8px', cursor: 'pointer',
                      background: isSelected ? 'rgba(79,70,229,0.15)' : isToday(day) ? 'rgba(79,70,229,0.06)' : 'var(--color-surface)',
                      border: isSelected ? '2px solid var(--color-gold)' : isToday(day) ? '1px solid var(--color-gold)' : '1px solid var(--color-border)',
                      transition: 'all 0.15s'
                    }}
                  >
                    <div style={{ fontWeight: isToday(day) ? 700 : 500, color: isToday(day) ? 'var(--color-gold)' : 'var(--color-text)', fontSize: '0.85rem', marginBottom: '4px' }}>{day}</div>
                    {appts.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
                        {appts.slice(0, 3).map(a => (
                          <div key={a.id} style={{ width: '8px', height: '8px', borderRadius: '50%', background: statusColor(a.status) }} title={a.client_name} />
                        ))}
                        {appts.length > 3 && <span style={{ fontSize: '0.6rem', color: 'var(--color-text-muted)' }}>+{appts.length - 3}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {selected && (
          <div className="card" style={{ padding: '1.5rem', alignSelf: 'start' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h4 style={{ margin: 0, color: 'var(--color-gold)', fontSize: '0.9rem' }}>
                {new Date(selected + 'T00:00:00').toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}
              </h4>
              <button className="btn btn-ghost" style={{ padding: '4px 8px' }} onClick={() => setSelected(null)}>✕</button>
            </div>
            {dayAppts.length === 0 ? (
              <p style={{ color: 'var(--color-text-muted)', textAlign: 'center' }}>Sin citas</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '500px', overflowY: 'auto' }}>
                {dayAppts.sort((a, b) => a.start_time?.localeCompare(b.appointment_time)).map(a => (
                  <div key={a.id} style={{ padding: '0.75rem', background: 'var(--color-surface)', borderRadius: '8px', borderLeft: `3px solid ${statusColor(a.status)}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontWeight: 700 }}>{to12h(a.start_time)}</span>
                      <span style={{ fontSize: '0.75rem', color: statusColor(a.status), fontWeight: 600 }}>{statusLabel(a.status)}</span>
                    </div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{a.client_name}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: '8px' }}>{a.service_name}</div>
                    {a.status === 'confirmed' && (
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                          disabled={updating === a.id}
                          onClick={() => changeStatus(a.id, 'attended')}>
                          Atendida
                        </button>
                        <button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                          disabled={updating === a.id}
                          onClick={() => changeStatus(a.id, 'not_attended')}>
                          No asistió
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
